import { ChangeDetectionStrategy, Component, inject, signal, input, output, ViewChild, computed, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, ActivatedRoute } from '@angular/router';
import { Location, NgClass } from '@angular/common';
import { OtpVerificationComponent } from '../otp-verification/otp-verification.component';
import { SignupStateService } from '../signup-state.service';
import { UserApiService } from 'src/app/shared/services/user-api.service';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ReturnUrlService } from 'src/app/shared/services/return-url.service';
import { AnalyticsUtils } from 'src/app/shared/services/analytics-utils';

@Component({
   selector: 'app-verify-email',
   templateUrl: './verify-email.component.html',
   imports: [OtpVerificationComponent, NgClass],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class VerifyEmailComponent implements OnInit {
   private router = inject(Router);
   private route = inject(ActivatedRoute);
   private location = inject(Location);
   private signupState = inject(SignupStateService);
   private userApiService = inject(UserApiService);
   private toastr = inject(ToastrService);
   private authService = inject(AuthService);
   private returnUrlService = inject(ReturnUrlService);
   private analyticsUtils = inject(AnalyticsUtils);

   isInsidePopup = input<boolean>(false);
   @ViewChild(OtpVerificationComponent) otpChild!: OtpVerificationComponent;

   isLoading = signal(false);
   isResending = signal(false);
   errorMessage = signal<string>('');
   email = signal(this.signupState.email() || 'user@example.com');
   emailVerified = output<void>();
   editEmailRequested = output<void>();

   private queryParamsSignal = toSignal(this.route.queryParams, { initialValue: {} as any });
   authQueryParams = computed(() => {
      const params = this.queryParamsSignal();
      const filtered: any = {};
      
      // Preserve source if valid
      const source = params['source'];
      if (source === 'trade' || source === 'academy') {
         filtered['source'] = source;
      }

      // Preserve return_url (Crucial for OAuth flow)
      if (params['return_url']) {
         filtered['return_url'] = params['return_url'];
      }

      // Preserve other relevant params
      if (params['redirect']) filtered['redirect'] = params['redirect'];
      if (params['featurebase']) filtered['featurebase'] = params['featurebase'];

      return filtered;
   });

   ngOnInit() {
      this.returnUrlService.captureReturnUrl();
   }

   async onVerify(otp: string) {
      if (otp.length === 4) {
         this.isLoading.set(true);
         const password = this.signupState.password();
         const email = this.signupState.email();

         if (!email || !password) {
            this.errorMessage.set('Signup information is missing. Please start again.');
            this.editEmailRequested.emit();
            return;
         }

         try {
            this.errorMessage.set('');
            const resp: any = await this.userApiService.verifyEmail(email, otp, password);
            if (resp.status) {
               this.toastr.success('Email verified successfully!');
               
               if (resp.data?.token) {
                  this.authService.setToken(resp.data.token);
                  this.analyticsUtils.sendDefaultSignup('email');
               }

               // Fetch onboarding config and sync user info (Step 2)
               try {
                  const userData = await this.userApiService.fetchUserDetailsAndSave();
                  const freeTrial = userData?.enable_free_trial ?? 0;
                  this.signupState.setOnboardingConfig(freeTrial);

                  // Ensure state email remains correct if service was empty
                  if (!this.signupState.email() && userData?.email) {
                     this.signupState.setSignupData(userData.email, this.signupState.password() || '');
                  }
               } catch (e) {
                  console.error('Failed to sync user data after verification', e);
               }

               if (this.isInsidePopup()) {
                  this.emailVerified.emit();
               } else {
                  this.router.navigate(['/authentication/activate'], { queryParams: this.authQueryParams() });
               }
            } else {
               this.errorMessage.set(resp.message || 'Verification failed. Please check your OTP.');
            }
         } catch (error: any) {
            console.error('Email verification error:', error);
            this.errorMessage.set(error?.error?.message || 'An error occurred during verification.');
         } finally {
            this.isLoading.set(false);
         }
      }
   }

   async resendOtp() {
      const email = this.signupState.email();
      if (!email) {
         this.errorMessage.set('Email information is missing. Please start again.');
         this.editEmailRequested.emit();
         return;
      }

      try {
         this.isResending.set(true);
         this.errorMessage.set('');
         const resp: any = await this.userApiService.sendEmailVerificationOtp(email);
         if (resp.status) {
            this.toastr.success('Verification OTP resent to your email');
            this.otpChild.startTimer();
         } else {
            this.errorMessage.set(resp.message || 'Failed to resend OTP.');
         }
      } catch (error: any) {
         console.error('Resend OTP error:', error);
         this.errorMessage.set(error?.error?.message || 'Failed to resend OTP. Please try again.');
      } finally {
         this.isResending.set(false);
      }
   }

   editEmail() {
      this.editEmailRequested.emit();
   }

   goBack() {
      this.editEmailRequested.emit();
   }
}
